import {computeDueDate, DueDateResult} from '@vat/engine/deadlines';
import {rangeOfPeriod} from '@vat/engine/month';
import type {LandlordTaxProfile, VatEngineRange, VatPeriodType, VatReturn, VatReturnStatus} from '@vat/engine/types';
import {buildVatReturnID, periodTypeForKey} from '../../services/vat-return.factory';
import {activeReturnForPeriod, periodLabel} from '../../services/vat-return-workflow.util';
import {VAT_COCKPIT_COPY} from '../../vat-copy';
import {deadlineTone, formatDueDateLabel, VatDeadlineTone} from '../shared/vat-deadline.util';

/**
 * Reine Cockpit-Logik (Spec 6 Screen 3): Zeitraum-Karten aus Profil (MONAT → 12, QUARTAL → 4) und
 * vorhandenen vatReturns-Dokumenten. Karten OHNE Dokument rechnen nichts („Noch nicht berechnet“);
 * Prüfpunkte kommen aus gespeicherter Prüfliste/Korrekturen (A1), nie aus einer Live-Berechnung.
 *
 * @author Anthony Nahas
 * @since 11.09.26
 */
export type VatCardStatus = VatReturnStatus | 'OFFEN';

export interface VatPeriodCard {
    periodKey: string;
    periodType: VatPeriodType;
    year: number;
    label: string;
    range: VatEngineRange;
    returnID: string;
    version: number;
    doc: VatReturn | null;
    status: VatCardStatus;
    uebermittelt: boolean;
    isCorrection: boolean;
    due: DueDateResult | null;
    dueLabel: string;
    dueTone: VatDeadlineTone;
    zahllast: number | null;
    checklistDone: number;
    checklistSkipped: number;
    adjustmentCount: number;
    hasPruefpunkte: boolean;
    canCorrect: boolean;
}

export const periodKeysFor = (profile: Pick<LandlordTaxProfile, 'voranmeldungszeitraum'>): string[] => {
    switch (profile.voranmeldungszeitraum) {
        case 'MONAT':
            return Array.from({length: 12}, (_, i) => `M${String(i + 1).padStart(2, '0')}`);
        case 'QUARTAL':
            return ['Q1', 'Q2', 'Q3', 'Q4'];
        default:
            return [];
    }
};

export interface BuildPeriodCardsParams {
    landlordID: string;
    profile: LandlordTaxProfile;
    docs: VatReturn[];
    year: number;
    now: Date;
}

export const buildPeriodCards = (p: BuildPeriodCardsParams): VatPeriodCard[] => {
    const docsOfYear = p.docs.filter(doc => doc.year === p.year);
    return periodKeysFor(p.profile).map(periodKey => {
        const doc = activeReturnForPeriod(docsOfYear, periodKey);
        const status: VatCardStatus = doc?.status ?? 'OFFEN';
        const due = p.profile.voranmeldungszeitraum === 'BEFREIT'
            ? null
            : computeDueDate({year: p.year, periodKey, dauerfrist: p.profile.dauerfristverlaengerung, bundesland: p.profile.bundesland, now: p.now});
        const entries = Object.values(doc?.checklist ?? {});
        const checklistDone = entries.filter(entry => entry.state === 'ERLEDIGT').length;
        const checklistSkipped = entries.filter(entry => entry.state === 'UEBERSPRUNGEN').length;
        const adjustmentCount = doc?.adjustments?.length ?? 0;
        return {
            periodKey,
            periodType: periodTypeForKey(periodKey),
            year: p.year,
            label: periodLabel(p.year, periodKey),
            range: rangeOfPeriod(p.year, periodKey),
            returnID: doc?.id ?? buildVatReturnID(p.landlordID, p.year, periodKey, 1),
            version: doc?.version ?? 1,
            doc,
            status,
            uebermittelt: !!doc?.uebermittelt,
            isCorrection: !!doc?.korrekturVonID,
            due,
            dueLabel: formatDueDateLabel(due, p.profile.dauerfristverlaengerung),
            dueTone: deadlineTone(due),
            zahllast: doc?.kennzahlen?.kz83 ?? null,
            checklistDone,
            checklistSkipped,
            adjustmentCount,
            hasPruefpunkte: entries.length > 0 || adjustmentCount > 0,
            canCorrect: status === 'GEPRUEFT' || status === 'EXPORTIERT',
        };
    });
};

export type VatCockpitStatusFilter = 'ALLE' | 'OFFEN' | 'GEPRUEFT' | 'EXPORTIERT' | 'BERICHTIGT' | 'UEBERFAELLIG';

export interface VatCockpitFilter {
    status: VatCockpitStatusFilter;
    onlyWithPruefpunkte: boolean;
}

export const DEFAULT_COCKPIT_FILTER: VatCockpitFilter = {status: 'ALLE', onlyWithPruefpunkte: false};

const isOpen = (card: VatPeriodCard): boolean => card.status === 'OFFEN' || card.status === 'ENTWURF';
const isOverdue = (card: VatPeriodCard): boolean => card.dueTone === 'overdue' && card.status !== 'EXPORTIERT' && !card.uebermittelt;

const matchesStatus = (card: VatPeriodCard, status: VatCockpitStatusFilter): boolean => {
    switch (status) {
        case 'ALLE':
            return true;
        case 'OFFEN':
            return isOpen(card);
        case 'GEPRUEFT':
            return card.status === 'GEPRUEFT';
        case 'EXPORTIERT':
            return card.status === 'EXPORTIERT';
        case 'BERICHTIGT':
            return card.isCorrection || card.status === 'BERICHTIGT';
        case 'UEBERFAELLIG':
            return isOverdue(card);
    }
};

export const filterCards = (cards: VatPeriodCard[], filter: VatCockpitFilter): VatPeriodCard[] =>
    cards.filter(card => matchesStatus(card, filter.status) && (!filter.onlyWithPruefpunkte || card.hasPruefpunkte));

export type VatKpiKey = 'OFFEN' | 'GEPRUEFT' | 'EXPORTIERT' | 'UEBERFAELLIG';

export interface VatKpi {
    key: VatKpiKey;
    label: string;
    value: number;
    tone: 'neutral' | 'info' | 'success' | 'danger';
}

export const buildKpis = (cards: VatPeriodCard[]): VatKpi[] => {
    const overdue = cards.filter(isOverdue).length;
    return [
        {key: 'OFFEN', label: VAT_COCKPIT_COPY.kpis.OFFEN, value: cards.filter(isOpen).length, tone: 'neutral'},
        {key: 'GEPRUEFT', label: VAT_COCKPIT_COPY.kpis.GEPRUEFT, value: cards.filter(card => card.status === 'GEPRUEFT').length, tone: 'info'},
        {key: 'EXPORTIERT', label: VAT_COCKPIT_COPY.kpis.EXPORTIERT, value: cards.filter(card => card.status === 'EXPORTIERT').length, tone: 'success'},
        {key: 'UEBERFAELLIG', label: VAT_COCKPIT_COPY.kpis.UEBERFAELLIG, value: overdue, tone: overdue > 0 ? 'danger' : 'neutral'},
    ];
};

/** Fristen-Strip: nächste Fristen aller noch nicht exportierten/übermittelten Zeiträume (überfällige zuerst). */
export const upcomingDeadlines = (cards: VatPeriodCard[], limit: number = 3): VatPeriodCard[] =>
    cards
        .filter(card => card.due !== null && card.status !== 'EXPORTIERT' && !card.uebermittelt)
        .sort((a, b) => a.due.dueDateEffective.getTime() - b.due.dueDateEffective.getTime())
        .slice(0, limit);

export const nextDeadline = (cards: VatPeriodCard[]): VatPeriodCard | null => upcomingDeadlines(cards, 1)[0] ?? null;

export const yearOptions = (now: Date, docs: Array<Pick<VatReturn, 'year'>>): number[] => {
    const years = new Set<number>([now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2]);
    docs.forEach(doc => years.add(doc.year));
    return [...years].sort((a, b) => b - a);
};
